import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import React, { useCallback, useState } from 'react'

import { Link } from 'react-router-dom'
import CircularLoading from '../../../../../components/CircularProgress'
import DistrubuteChannelTag from '../../../../../components/DistrubuteChannelTag'
import OrderTag from '../../../../../components/OrderTag'
import CrmTag from '../../../../../components/OrderTag/crmTag'
import OverdueTag from '../../../../../components/OrderTag/overdue'
import PrintedTag from '../../../../../components/OrderTag/printed'
import TelesaleTag from '../../../../../components/OrderTag/telesale'
import Pagination from '../../../../../components/Pagination'
import ProductItemDetailTag from '../../../../../components/ProductItemDetailTag'
import { Tag } from '../../../../../components/Tag'
import TrackingProviderImage from '../../../../../components/TrackingProviderImage'
import {
  ACTION_TYPE_TITLES,
  ONLY_DATE_FORMAT,
  ONLY_DATE_TIME_FORMAT,
} from '../../../../../constants/global'
import PageUrls from '../../../../../constants/page-urls'
import { useUser } from '../../../../../hooks/useUser'
import {
  EOrderStatus,
  EOrderType,
  IOrder,
  IOrderItem,
  OrderRecipient,
  OrderShippingProvider,
} from '../../../../../types/order'
import { IFilterMapped } from '../../../components/OrderFilter'
import FacebookTag from '../../../../../components/OrderTag/facebookTag'
import TrackingTimelineModal from '../../../../../components/Modal/TrackingTimelineModal'
import { FiCopy } from 'react-icons/fi'
import OrderDetailModal from './OrderDetailModal'
import PaginationButton from '../../../../../components/Pagination/PaginationButton'

interface IOrderTableProps {
  selectedData: IOrder[]
  setSelectedData: (orders: IOrder[]) => void
  isLoading: boolean
  data: IOrder[]
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
  statusOrder: EOrderStatus | string
  cursor?: string
  cursorPrev?: string
  onCursorChange?: (cursor: string | undefined, isPrev: boolean) => void
  page?: number
}

const OrderListTable = ({
  selectedData,
  setSelectedData,
  isLoading,
  data,
  filter,
  onFilterChange,
  handlePageChange,
  total,
  statusOrder,
  cursor,
  cursorPrev,
  onCursorChange,
  page = 1,
}: IOrderTableProps) => {
  const { merchant } = useUser()
  const showSocial = !!merchant?.config?.display?.social

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    const keys = newSelectedRowKeys as string[]
    const orderObjects = data?.reduce<{ [key: string]: IOrder }>((acc, cur) => {
      acc[cur.objectId] = cur
      return acc
    }, {})
    const orders = keys.map((key: string) => {
      return orderObjects[key]
    })
    setSelectedData(orders)
  }

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [order, setOrder] = useState<IOrder | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false)
  const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null)

  const handleShowTracking = useCallback((order: IOrder) => {
    setIsOpen(true)
    setOrder(order)
  }, [])

  const handleShowDetail = useCallback((order: IOrder) => {
    setIsDetailOpen(true)
    setSelectedOrder(order)
  }, [])

  const handleCopyText = useCallback((text: string) => {
    navigator.clipboard.writeText(text ?? '')
    message.success(`คัดลอกแล้ว: ${text ?? ''}`)
  }, [])

  const columns: TableColumnsType<IOrder & { key: string; numberRow: number }> =
    [
      {
        title: 'ลำดับ',
        dataIndex: 'numberRow',
        key: 'numberRow',
        align: 'center' as const,
        width: 80,
        render: (numberRow: number) => {
          return (
            <Box>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                textAlign={'center'}
              >
                {numberRow}
              </Typography>
            </Box>
          )
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'status',
        key: 'status',
        align: 'center' as const,
        render: (status: string, order: IOrder) => {
          const lastTracking =
            order.trackings?.[order.trackings?.length - 1] ??
            ({} as { trackingDate: string })
          return (
            <>
              {order.trackingCode && order.trackingProvider && (
                <Box mb={1}>
                  <TrackingProviderImage
                    trackingProvider={
                      order.trackingProvider as OrderShippingProvider
                    }
                  />
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      textAlign={'center'}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleShowTracking(order)}
                    >
                      {order.trackingCode}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleCopyText(order?.trackingCode ?? '')}
                    >
                      <FiCopy size={12} />
                    </IconButton>
                  </Box>
                  <Typography fontSize={10}>
                    {lastTracking?.trackingDate
                      ? dayjs(lastTracking.trackingDate).format(
                          ONLY_DATE_TIME_FORMAT,
                        )
                      : ''}
                  </Typography>
                </Box>
              )}
              <Box>
                <OrderTag order={order} status={status as EOrderStatus} />
              </Box>
              {order.providerStatusText && (
                <Box
                  pt={1}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleShowTracking(order)}
                >
                  <Tag color={`success`}>{order.providerStatusText}</Tag>
                </Box>
              )}
            </>
          )
        },
      },
      {
        title: 'เลขออเดอร์',
        dataIndex: 'code',
        key: 'code',
        align: 'center' as const,
        render: (code: string, order: IOrder) => (
          <>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={0.5}
            >
              <Typography
                variant="subtitle1"
                color="text.primary"
                component={Link}
                to={`${PageUrls.Orders.Detail}/${order.objectId}`}
                sx={{ cursor: 'pointer' }}
              >
                {code}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopyText(`${order?.code ?? ''}`)}
              >
                <FiCopy size={12} />
              </IconButton>
            </Box>
            <Typography fontSize={11}>
              {order.orderDate
                ? dayjs(order.orderDate).format(ONLY_DATE_FORMAT)
                : ''}
            </Typography>
          </>
        ),
      },
      {
        title: 'ราคา',
        dataIndex: 'total',
        key: 'total',
        align: 'center' as const,
        render: (total: string, order: IOrder) => (
          <Grid
            container
            flexDirection={'column'}
            textAlign={'center'}
            minWidth={120}
          >
            <Grid item>
              <Typography
                variant="h5"
                color={
                  order?.paymentMethod === EOrderType.COD ? 'red' : 'green'
                }
              >
                {total.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1 }}
              >
                {order?.paymentMethod === EOrderType.COD
                  ? 'เก็บเงินปลายทาง'
                  : 'โอนผ่านธนาคาร'}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {order.items.length} รายการ/{order.quantity} ชิ้น
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      {
        title: 'จัดส่ง',
        dataIndex: 'recipient',
        key: 'recipient',
        align: 'center' as const,
        render: (recipient: OrderRecipient) => (
          <Grid
            container
            flexDirection={'column'}
            minWidth={200}
            textAlign={'left'}
          >
            <Grid item>
              <Typography variant="subtitle1">{recipient?.name}</Typography>
            </Grid>
            <Grid item>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ marginTop: 1, wordBreak: 'break-all' }}
              >
                {recipient?.address} {recipient?.subDistrict}{' '}
                {recipient?.district} {recipient?.province} {recipient?.zipcode}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="subtitle1">{recipient?.phone}</Typography>
            </Grid>
            {showSocial && recipient?.social && (
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  title="โซเชียลของลูกค้า"
                >
                  <AlternateEmailIcon
                    sx={{ fontSize: 14 }}
                    color="disabled"
                  />
                  <Typography variant="subtitle2" color="text.secondary">
                    {recipient.social}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        title: 'สินค้า',
        dataIndex: 'items',
        key: 'items',
        align: 'center' as const,
        render: (items: IOrderItem[]) => (
          <Box minWidth={150}>
            {items?.map((item: IOrderItem, index: number) => {
              return (
                <ProductItemDetailTag
                  key={index}
                  item={{
                    name: item.productName,
                    quantity: item.quantity,
                    isCollection: item.isCollection,
                    url: item.imageUrl,
                    color: item.meta?.color,
                    size: item.meta?.size,
                  }}
                />
              )
            })}
          </Box>
        ),
      },
      {
        title: 'หมายเหตุ',
        dataIndex: 'note',
        key: 'note',
        width: 200,
        align: 'center' as const,
        render: (note: string, order: IOrder) => {
          return (
            <>
              <DistrubuteChannelTag distribution={order.distribution} />
              {note && (
                <Typography variant="subtitle2" textAlign={'center'}>
                  {note}
                </Typography>
              )}
              <TelesaleTag order={order} />
              <CrmTag order={order} />
              <OverdueTag order={order} />
              <PrintedTag order={order} />
              <FacebookTag order={order} />
            </>
          )
        },
      },
      {
        title: 'วันที่เพิ่ม',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 120,
        align: 'center' as const,
        render: (createdAt: string, order: IOrder) => (
          <Grid container flexDirection={'column'} textAlign={'center'}>
            <Grid item>
              <Typography variant="subtitle1">{order?.actor}</Typography>
            </Grid>
            <Grid item>
              <Typography
                sx={{ mb: 0, lineHeight: 1.1 }}
                fontSize={12}
                variant="subtitle2"
                color="text.secondary"
              >
                {dayjs(createdAt).format(ONLY_DATE_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        ),
      },
      // {
      //   title: 'วันที่แก้ไข',
      //   dataIndex: 'updatedAt',
      //   key: 'updatedAt',
      //   width: 120,
      //   align: 'center' as const,
      //   render: (updatedAt: string, order: IOrder) => {
      //     const lastAction =
      //       order.actions?.[order.actions?.length - 1] ??
      //       ({} as {
      //         actor: string
      //         date: string
      //         createdAt: string
      //         action: string
      //       })
      //     return (
      //       <Grid container flexDirection={'column'} textAlign={'center'}>
      //         <Grid item>
      //           <Typography variant="subtitle1">
      //             {lastAction?.actor ?? ''}
      //           </Typography>
      //         </Grid>
      //         <Grid item>
      //           <Typography
      //             variant="subtitle2"
      //             fontSize={12}
      //             sx={{ mb: 1, lineHeight: 1.1 }}
      //             color="text.secondary"
      //           >
      //             {dayjs(lastAction?.date ?? lastAction?.createdAt).format(
      //               ONLY_DATE_TIME_FORMAT,
      //             )}
      //           </Typography>
      //         </Grid>
      //         <Grid item>
      //           <Tag color="success">
      //             {ACTION_TYPE_TITLES?.[lastAction?.action ?? ''] ??
      //               lastAction?.action ??
      //               'ไม่ระบุ'}
      //           </Tag>
      //         </Grid>
      //       </Grid>
      //     )
      //   },
      // },
      {
        dataIndex: '',
        key: 'action',
        width: 60,
        align: 'center' as const,
        render: (_: undefined, order: IOrder) => (
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <IconButton onClick={() => handleShowDetail(order)}>
                <InfoIcon />
              </IconButton>
            </Grid>
          </Grid>
        ),
      },
    ]

  const rowSelection = {
    selectedRowKeys: selectedData.map((order: IOrder) => order.objectId),
    onChange: onSelectChange,
  }

  // const enableSelectRow = [EOrderStatus.PENDING].includes(
  //   statusOrder as EOrderStatus,
  // )

  const enableSelectRow = ![
    EOrderStatus.CANCLELLED,
    EOrderStatus.RETURNED,
  ].includes(statusOrder as EOrderStatus)

  return (
    <Box>
      <Table
        // rowSelection={{
        //   type: 'checkbox',
        //   selectedRowKeys: selectedData.map((item) => item.objectId),
        //   onChange: onSelectChange,
        // }}
        columns={columns}
        dataSource={data?.map((item, index) => ({
          ...item,
          key: item.objectId,
          numberRow: page == 1 ? index + 1 : (page - 1) * 20 + index + 1,
        }))}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 1500 }}
      />
      <PaginationButton
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) =>
          onFilterChange({ ...filter, page, perPage })
        }
        cursor={cursor}
        cursorPrev={cursorPrev}
        onCursorChange={onCursorChange}
      />
      <TrackingTimelineModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        order={order}
        title={`ประวัติการจัดส่ง ${order?.trackingCode ?? ''}`}
      />
      <OrderDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        order={selectedOrder}
      />
    </Box>
  )
}

export default OrderListTable
